/**
 * Controls for the two layers that describe ground rather than air.
 *
 * They live next to the layer picker and appear only while their layer is
 * selected — both are meaningless otherwise, and the panel is already long.
 */

import { useStore } from '../state/store';

const metresToFeet = (m: number) => Math.round((m * 3.28084) / 10) * 10;

export function WatermarkControl() {
  const range = useStore((s) => s.terrainRange);
  const altitude = useStore((s) => s.layers.watermarkAltitude);
  const setLayers = useStore((s) => s.setLayers);

  if (!range) return null;
  const [minH, maxH] = range;
  // Round outward so the ends of the slider can actually reach the valley floor
  // and the summit rather than stopping just short of both.
  const lo = Math.floor(minH / 25) * 25;
  const hi = Math.ceil(maxH / 25) * 25;

  return (
    <div className="field">
      <div className="field-head">
        <label htmlFor="watermark-alt">Altitude</label>
        <span className="field-value">
          {Math.round(altitude)} m · {metresToFeet(altitude)} ft
        </span>
      </div>
      <input
        id="watermark-alt"
        type="range"
        min={lo}
        max={hi}
        step={25}
        value={Math.min(hi, Math.max(lo, altitude))}
        onChange={(e) => setLayers({ watermarkAltitude: Number(e.target.value) })}
      />
      <div className="range-ends">
        <span>{lo} m</span>
        <span>{hi} m</span>
      </div>
      <p className="hint">
        Floods everything below the line, shaded by how hard the terrain is
        squeezing the flow through it. This is the gradient wind being channelled
        by the valley — thermally driven valley and slope winds are not modelled.
      </p>
    </div>
  );
}

export function HikeFlyControl() {
  const withWind = useStore((s) => s.layers.hikeFlyWind);
  const ready = useStore((s) => s.hikeFlyReady);
  const setLayers = useStore((s) => s.setLayers);

  return (
    <div className="field">
      <div className="toggle-row">
        <input
          id="hikefly-wind"
          type="checkbox"
          checked={withWind}
          onChange={(e) => setLayers({ hikeFlyWind: e.target.checked })}
        />
        <label htmlFor="hikefly-wind">Account for today’s wind</label>
      </div>
      <p className="hint">
        {withWind
          ? 'Slopes facing away from the wind, and ground inside a rotor zone, fade ' +
            'out — and the glide asked of your wing rises with the headwind you would ' +
            'launch into, since a headwind cuts ground glide without changing your ' +
            'sink. Half your trim speed on the nose halves your glide.'
          : 'Terrain only: the glide needed to reach open ground at least 300 ft below, ' +
            'clearing everything in between. Lower is better.'}
      </p>
      <p className="hint">
        Elevation models are bare earth. Forest, powerlines, crops, buildings and
        access are all invisible here — a flat patch is flat ground, not a
        landing field. {!ready && 'Computing…'}
      </p>
    </div>
  );
}
